import React, { useEffect, useMemo, useState } from "react"
import { Box, Button, HStack, Spinner, Text, VStack, useToast } from "@chakra-ui/react"
import { useTranslation } from "next-i18next"
import { normalizeCommentsFromInvoice } from "./commentModel"
import CommentThread from "./CommentThread"
import MentionTextarea from "./MentionTextarea"
import { formatMentionsForApi, normalizeMentionCandidates } from "./mentionUtils"
import type { MentionUserCandidate } from "./mentionUtils"

type InvoiceCommentsSectionProps = {
  apiUrl: string
  invoice: any
  invoiceId?: string
  session: any
  userData: any
  onRefresh: () => Promise<any>
  isDisabled?: boolean
}

async function apiCall(
  apiUrl: string,
  path: string,
  accessToken: string,
  method: "POST" | "PUT" | "DELETE",
  body?: any,
) {
  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  // Some endpoints might return empty body (201/200). Handle both.
  const text = await res.text().catch(() => "")
  const json = text ? (() => {
    try {
      return JSON.parse(text)
    } catch {
      return undefined
    }
  })() : undefined

  if (!res.ok) {
    const msg =
      (json && (json.message?.[0] || json.message || json.error)) ||
      text ||
      `${res.status} ${res.statusText}`
    throw new Error(String(msg))
  }
  return json
}

export default function InvoiceCommentsSection({
  apiUrl,
  invoice,
  invoiceId,
  session,
  userData,
  onRefresh,
  isDisabled,
}: InvoiceCommentsSectionProps) {
  const { t } = useTranslation("common")
  const toast = useToast()
  const [text, setText] = useState("")
  const [isBusy, setIsBusy] = useState(false)
  const [localComments, setLocalComments] = useState<any[]>([])

  const candidates = useMemo(() => normalizeMentionCandidates(userData), [userData])

  const byId = useMemo(() => {
    const m = new Map<string, MentionUserCandidate>()
    for (const u of candidates) m.set(u._id, u)
    return m
  }, [candidates])

  useEffect(() => {
    const initial =
      (Array.isArray(invoice?.comments) && invoice.comments) ||
      (Array.isArray(invoice?.comment) && invoice.comment) ||
      (Array.isArray(invoice?.commentThreads) && invoice.commentThreads) ||
      []
    setLocalComments(initial)
  }, [invoiceId, invoice])

  const comments = useMemo(() => normalizeCommentsFromInvoice({ comments: localComments }), [localComments])
  const canMutate = Boolean(!isDisabled && invoiceId && session?.accessToken)

  const extractComments = (inv: any) => {
    if (Array.isArray(inv?.comments)) return inv.comments
    if (Array.isArray(inv?.comment)) return inv.comment
    if (Array.isArray(inv?.commentThreads)) return inv.commentThreads
    return []
  }

  const reloadComments = async () => {
    if (!invoiceId || !session?.accessToken) return
    const res = await fetch(`${apiUrl}/invoices/${invoiceId}`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    const json = await res.json().catch(() => ({}))
    setLocalComments(extractComments(json))
  }

  const refresh = async () => {
    // Keep scroll stable by only reloading comments locally.
    // Still call onRefresh in background for global state consistency, but do not await it.
    try {
      await reloadComments()
    } catch {
      // ignore reload errors; mutation already succeeded
    }
    try {
      void onRefresh?.()
    } catch {
      // ignore
    }
  }

  const addComment = async () => {
    if (!invoiceId || !session?.accessToken) return
    const prepared = formatMentionsForApi(text.trim(), candidates)
    const payload = { text: prepared.text, mentionedUserIds: prepared.mentionedUserIds }

    setIsBusy(true)
    try {
      await apiCall(apiUrl, `/invoices/${invoiceId}/comments`, session.accessToken, "POST", payload)
      setText("")
      await refresh()
    } catch (e: any) {
      toast({
        title: t("comments.errorTitle"),
        description: e?.message || t("comments.errorGeneric"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const reply = async (commentId: string, replyText: string) => {
    if (!invoiceId || !session?.accessToken) return
    const prepared = formatMentionsForApi(replyText.trim(), candidates)
    const payload = { text: prepared.text, mentionedUserIds: prepared.mentionedUserIds }

    setIsBusy(true)
    try {
      await apiCall(
        apiUrl,
        `/invoices/${invoiceId}/comments/${commentId}/replies`,
        session.accessToken,
        "POST",
        payload,
      )
      await refresh()
    } catch (e: any) {
      toast({
        title: t("comments.errorTitle"),
        description: e?.message || t("comments.errorGeneric"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const deleteComment = async (commentId: string) => {
    if (!invoiceId || !session?.accessToken) return
    setIsBusy(true)
    try {
      await apiCall(apiUrl, `/invoices/${invoiceId}/comments/${commentId}`, session.accessToken, "DELETE")
      await refresh()
    } catch (e: any) {
      toast({
        title: t("comments.errorTitle"),
        description: e?.message || t("comments.errorGeneric"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const deleteReply = async (commentId: string, replyId: string) => {
    if (!invoiceId || !session?.accessToken) return
    setIsBusy(true)
    try {
      await apiCall(
        apiUrl,
        `/invoices/${invoiceId}/comments/${commentId}/replies/${replyId}`,
        session.accessToken,
        "DELETE",
      )
      await refresh()
    } catch (e: any) {
      toast({
        title: t("comments.errorTitle"),
        description: e?.message || t("comments.errorGeneric"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const toggleDone = async (commentId: string) => {
    if (!invoiceId || !session?.accessToken) return
    setIsBusy(true)
    try {
      await apiCall(apiUrl, `/invoices/${invoiceId}/comments/${commentId}/status`, session.accessToken, "PUT")
      await refresh()
    } catch (e: any) {
      toast({
        title: t("comments.errorTitle"),
        description: e?.message || t("comments.errorGeneric"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      })
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <VStack align="stretch" spacing={4}>
      {!invoiceId ? (
        <Text fontSize="sm" color="gray.600">
          {t("comments.notAvailable")}
        </Text>
      ) : null}

      {comments.length === 0 ? (
        <Text fontSize="sm" color="gray.600">
          {t("comments.empty")}
        </Text>
      ) : (
        <CommentThread
          comments={comments}
          candidates={candidates}
          getUser={(id) => byId.get(id)}
          currentUserId={(session as any)?.user_data?._id}
          isBusy={isBusy}
          canMutate={canMutate}
          onReply={reply}
          onDeleteComment={deleteComment}
          onDeleteReply={deleteReply}
          onToggleDone={toggleDone}
        />
      )}

      <Box pt={2}>
        <MentionTextarea
          value={text}
          onChange={setText}
          candidates={candidates}
          placeholder={t("comments.placeholder")}
          isDisabled={!canMutate || isBusy}
          onSubmit={addComment}
          actionLabel={t("comments.send")}
          onAction={addComment}
          isActionLoading={isBusy}
          isActionDisabled={!text.trim()}
        />
      </Box>
    </VStack>
  )
}


